import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Flag, Pencil, Trash2, Loader2, Send } from "lucide-react";

const SCOPES = [["community", "Community"], ["builtin", "Built-in"], ["favorites", "Favorites"], ["mine", "My dares"]];
const CATS = [["funny", "Funny"], ["flirty", "Flirty"], ["naughty", "Naughty"], ["after_dark", "After Dark 18+"]];
const STATUS_TONE = { pending: "text-amber-300", approved: "text-emerald-300", rejected: "text-rose-300", removed: "text-zinc-500" };

function ChallengeForm({ initial, onSaved, onCancel }) {
  const [type, setType] = useState(initial?.type || "dare");
  const [category, setCategory] = useState(initial?.category || "funny");
  const [text, setText] = useState(initial?.text || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (busy || text.trim().length < 8) return;
    setBusy(true);
    try {
      const body = { type, category, text: text.trim() };
      const { data } = initial?.id ? await api.patch(`/dare/library/${initial.id}`, body) : await api.post("/dare/library", body);
      if (data.status === "rejected") toast.error(data.review_note || "Blocked by content moderation.");
      else toast.success(initial?.id ? "Updated — back in the review queue." : "Submitted! It goes live for all hosts once approved.");
      setText("");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid="dare-submit-form">
      <div className="flex gap-2">
        {["dare", "truth"].map((t) => (
          <button key={t} type="button" data-testid={`dare-form-type-${t}`} onClick={() => setType(t)} className={`px-3 py-1 rounded-full border text-xs capitalize ${type === t ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10"}`}>{t}</button>
        ))}
        <select data-testid="dare-form-category" value={category} onChange={(e) => setCategory(e.target.value)} className="ml-auto bg-white/5 border border-white/10 rounded-full px-2 py-1 text-xs text-white">
          {CATS.map(([k, l]) => <option key={k} value={k} className="bg-zinc-900">{l}</option>)}
        </select>
      </div>
      <textarea data-testid="dare-form-text" value={text} onChange={(e) => setText(e.target.value.slice(0, 240))} rows={2} placeholder={type === "dare" ? "Describe the dare (keep it fun, non-explicit, anyone can Pass)" : "Ask a truth question"} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50 resize-none" />
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-zinc-500">{text.length}/240 · reviewed by MyPandaX before it&apos;s shared</span>
        {onCancel && <button type="button" onClick={onCancel} className="ml-auto text-xs text-zinc-400">Cancel</button>}
        <button type="button" data-testid="dare-form-submit" onClick={save} disabled={busy || text.trim().length < 8} className={`${onCancel ? "" : "ml-auto"} flex items-center gap-1 bg-yellow-400 disabled:opacity-50 text-black font-bold px-3 py-1.5 rounded-full text-xs`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {initial?.id ? "Save" : "Submit"}
        </button>
      </div>
    </div>
  );
}

function Row({ c, onFav, onReport, onEdit, onDelete }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm" data-testid={`dare-lib-item-${c.id}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
        <span className={c.type === "dare" ? "text-orange-300" : "text-sky-300"}>{c.type}</span>
        <span>· {CATS.find(([k]) => k === c.category)?.[1] || c.category}</span>
        {c.mine && <span className={`ml-auto normal-case tracking-normal ${STATUS_TONE[c.status]}`} data-testid={`dare-lib-status-${c.id}`}>{c.status}</span>}
        {!c.mine && c.author_name && <span className="ml-auto normal-case tracking-normal">by {c.author_name}</span>}
      </div>
      <div className="text-white">{c.text}</div>
      {c.mine && c.review_note && <div className="text-[11px] text-rose-300 mt-1">{c.review_note}</div>}
      <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
        {c.status === "approved" && <button type="button" data-testid={`dare-lib-fav-${c.id}`} onClick={() => onFav(c)} className={`flex items-center gap-1 ${c.favorite ? "text-pink-300" : ""}`}><Heart size={13} fill={c.favorite ? "currentColor" : "none"} /> {c.favorite ? "Saved" : "Save"}</button>}
        {!c.builtin && !c.mine && <button type="button" data-testid={`dare-lib-report-${c.id}`} onClick={() => onReport(c)} className="flex items-center gap-1 hover:text-rose-300"><Flag size={13} /> Report</button>}
        {c.mine && <button type="button" data-testid={`dare-lib-edit-${c.id}`} onClick={() => onEdit(c)} className="flex items-center gap-1"><Pencil size={13} /> Edit</button>}
        {c.mine && <button type="button" data-testid={`dare-lib-delete-${c.id}`} onClick={() => onDelete(c)} className="flex items-center gap-1 hover:text-rose-300"><Trash2 size={13} /> Delete</button>}
      </div>
    </div>
  );
}

/** Shared custom-dare library: community (approved), built-ins, favorites, my submissions. */
export default function DareLibrarySheet({ open, onOpenChange }) {
  const [scope, setScope] = useState("community");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/dare/library", { params: { scope } }); setItems(data.items); } catch { setItems([]); }
  }, [scope]);
  useEffect(() => { if (open) { setItems(null); load(); } }, [open, load]);

  const fav = async (c) => { try { await api.post(`/dare/library/${c.id}/favorite`); load(); } catch { toast.error("Could not save favorite."); } };
  const report = async (c) => {
    const reason = window.prompt("Why is this challenge inappropriate?");
    if (!reason) return;
    try { await api.post(`/dare/library/${c.id}/report`, { reason }); toast.success("Reported — thanks, our team will review it."); } catch { toast.error("Could not report."); }
  };
  const del = async (c) => {
    if (!window.confirm("Delete this challenge?")) return;
    try { await api.delete(`/dare/library/${c.id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete."); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="dare-library-sheet" className="z-[130] bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 text-white rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader><SheetTitle className="text-white text-sm uppercase tracking-widest">Custom Dares Library</SheetTitle></SheetHeader>
        <div className="mt-3 space-y-3 pb-3">
          {editing ? <ChallengeForm key={editing.id} initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /> : <ChallengeForm key="new" onSaved={() => { if (scope === "mine") load(); else setScope("mine"); }} />}
          <div className="flex gap-1.5 overflow-x-auto">
            {SCOPES.map(([k, l]) => (
              <button key={k} type="button" data-testid={`dare-lib-scope-${k}`} onClick={() => setScope(k)} className={`shrink-0 px-3 py-1.5 rounded-full border text-xs ${scope === k ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10 text-zinc-300"}`}>{l}</button>
            ))}
          </div>
          {items === null ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500 py-3"><Loader2 size={13} className="animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-xs text-zinc-500 py-3 text-center" data-testid="dare-lib-empty">{scope === "mine" ? "You haven't submitted any dares yet." : scope === "favorites" ? "No favorites yet — tap Save on any challenge." : "Nothing here yet."}</div>
          ) : (
            <div className="space-y-2">{items.map((c) => <Row key={c.id} c={c} onFav={fav} onReport={report} onEdit={setEditing} onDelete={del} />)}</div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
